import {Component, computed, inject, input, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {DocumentService, DocumentSummary} from '../../../../../api';
import {ElciaButton} from '../../../../components/buttons/button/button';

// Extensions that browsers can render inline in a tab, mapped to the MIME type
// we force on the blob so it previews instead of downloading.
const PREVIEW_MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

@Component({
  selector: 'elcia-document-item',
  imports: [DatePipe, ElciaButton],
  templateUrl: './document-item.html',
})
export class DocumentItem {
  private readonly documentService = inject(DocumentService);

  document = input.required<DocumentSummary>();
  onDownload = output<void>();
  onDelete = output<void>();

  protected previewMimeType = computed(() => {
    const ext = this.document().name.split('.').pop()?.toLowerCase() ?? '';
    return PREVIEW_MIME_TYPES[ext] ?? null;
  });

  protected preview(): void {
    const mimeType = this.previewMimeType();
    if (!mimeType) return;
    // Open the tab synchronously (within the click gesture) so it survives popup blockers,
    // then point it at the blob once the download resolves.
    const tab = window.open('', '_blank');
    this.documentService.downloadDocument(this.document().id).subscribe(blob => {
      const url = URL.createObjectURL(new Blob([blob], {type: mimeType}));
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
  }

  protected getFileIcon(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pi pi-file-pdf text-red-500';
    if (['doc', 'docx'].includes(ext ?? '')) return 'pi pi-file-word text-blue-500';
    if (['xls', 'xlsx'].includes(ext ?? '')) return 'pi pi-file-excel text-green-500';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext ?? '')) return 'pi pi-image text-purple-500';
    return 'pi pi-file text-surface-500';
  }
}
